import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { aboutConfig as defaultAboutConfig } from '@/config';
import { useConfig } from '@/lib/siteData';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { imageVariants, withWebp } from '@/lib/imageSrc';

export function About({ index }: { index?: number } = {}) {
  const aboutConfig = useConfig('about_config', defaultAboutConfig);
  const sectionRef = useRef<HTMLElement>(null);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    const root = sectionRef.current;
    if (!root) return;

    // Reveal every `.anim-item`. `instant` short-circuits the fade/stagger for
    // reduced-motion users; the default path keeps the existing i*100ms stagger
    // and per-element transition. Only ever transitions 0→1, never back, so the
    // fail-open fallback below can never cause a show-then-hide flash.
    const reveal = (instant: boolean) => {
      const animElements = root.querySelectorAll('.anim-item');
      animElements.forEach((node, i) => {
        const el = node as HTMLElement;
        const apply = () => {
          if (instant) el.style.transition = 'none';
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
        };
        if (instant) apply();
        else setTimeout(apply, i * 100);
      });
    };

    // Honor prefers-reduced-motion: reveal immediately, no observer, no timers.
    if (prefersReducedMotion) {
      reveal(true);
      return;
    }

    // Fail-open: if IntersectionObserver is unavailable (unsupported / headless),
    // reveal right away rather than leaving the copy permanently invisible.
    if (typeof IntersectionObserver === 'undefined') {
      reveal(false);
      return;
    }

    let revealed = false;
    const fireReveal = () => {
      if (revealed) return;
      revealed = true;
      reveal(false);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            fireReveal();
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    observer.observe(root);

    // Fail-open safety net: if the observer never fires (already in viewport at
    // mount, never scrolled, capture/headless), reveal anyway after ~1.2s.
    const fallbackTimer = setTimeout(fireReveal, 1200);

    return () => {
      clearTimeout(fallbackTimer);
      observer.disconnect();
    };
  }, [prefersReducedMotion]);

  if (aboutConfig.visible === false) return null;

  // Per-surface overrides. Each `show.<field>` defaults to true when the
  // toggle is absent / undefined; admin must explicitly set false to hide.
  const show = aboutConfig.showOnHome ?? {};
  const portrait = aboutConfig.portraitImageHome || aboutConfig.portraitImage || '/images/portrait-office-1.jpg';
  // Portrait sits in a 5/12 column of a max-w-[1200px] grid → ~460px at lg,
  // full-bleed below. Null for admin uploads / unknown paths, which then render
  // the plain <img> unchanged.
  const portraitVariants = imageVariants(portrait, '(min-width: 1024px) 460px, 100vw');

  return (
    <section
      ref={sectionRef}
      id="about"
      className="w-full py-16 md:py-20 bg-surface-elevated border-t border-surface-border-gray"
    >
      <div className="w-full max-w-[1200px] mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column — Portrait + Quick Facts */}
          <div className="lg:col-span-5">
            {show.portrait !== false && (
              <div className="anim-item relative" style={{ opacity: 0, transform: 'translateY(30px)', transition: 'all 0.8s cubic-bezier(0.165, 0.840, 0.440, 1)' }}>
                <div className="rounded-2xl overflow-hidden bg-surface-gray">
                  {withWebp(
                    portraitVariants,
                    <img
                      src={portrait}
                      alt="Chinedu Koggu — FinTech Techpreneur"
                      loading="lazy"
                      decoding="async"
                      width={800}
                      height={800}
                      className="w-full aspect-square object-cover object-top"
                    />,
                  )}
                </div>
                {/* Floating badge */}
                {show.experienceBadge !== false && (
                  <div className="absolute -bottom-4 -right-2 md:right-4 bg-surface-dark text-white px-5 py-3 rounded-xl shadow-xl">
                    <p className="font-heading text-2xl font-bold">20+</p>
                    <p className="font-mono-label text-[10px] uppercase tracking-wider text-white/50">Years Building</p>
                  </div>
                )}
              </div>
            )}

            {/* Quick facts row — render the full stats list (canonical source).
                The dedicated About page shows the same 4 entries, so editors don't
                have to maintain two parallel "first 3" vs "all 4" lists. */}
            {show.stats !== false && aboutConfig.stats.length > 0 && (
              <div className="anim-item grid grid-cols-2 gap-3 mt-10 sm:grid-cols-3" style={{ opacity: 0, transform: 'translateY(20px)', transition: 'all 0.6s ease-out 0.4s' }}>
                {aboutConfig.stats.map((stat) => (
                  <div
                    key={stat.label}
                    role="group"
                    aria-label={`${stat.value} ${stat.label}`}
                    className="text-center p-4 md:p-5 bg-surface-gray rounded-xl border border-surface-border-gray"
                  >
                    <p aria-hidden="true" className="font-heading text-base md:text-lg font-bold text-text-primary whitespace-nowrap">{stat.value}</p>
                    <p aria-hidden="true" className="font-mono-label text-[11px] text-text-light uppercase tracking-wider mt-1 leading-snug">{stat.label}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Column — Story */}
          <div className="lg:col-span-7">
            {show.label !== false && (
              <span
                className="anim-item font-mono-label text-xs uppercase tracking-[0.2em] text-text-light inline-block"
                style={{ opacity: 0, transform: 'translateY(30px)', transition: 'all 0.8s cubic-bezier(0.165, 0.840, 0.440, 1)' }}
              >
                {index != null && <span className="text-accent-blue mr-1">{String(index).padStart(2, '0')} — </span>}
                {aboutConfig.label}
              </span>
            )}
            {show.heading !== false && (
              <h2
                className="anim-item font-geist-mono text-3xl md:text-[2.5rem] font-medium text-text-primary mt-4 leading-[1.1] tracking-tight"
                style={{ opacity: 0, transform: 'translateY(30px)', transition: 'all 0.8s cubic-bezier(0.165, 0.840, 0.440, 1) 0.1s' }}
              >
                {aboutConfig.heading || 'From Engineering to Ecosystems'}
              </h2>
            )}

            {show.body !== false && (
              <div
                className="anim-item mt-6 space-y-4"
                style={{ opacity: 0, transform: 'translateY(30px)', transition: 'all 0.8s cubic-bezier(0.165, 0.840, 0.440, 1) 0.2s' }}
              >
                {aboutConfig.body ? (
                  <p className="font-body text-base text-text-gray leading-relaxed whitespace-pre-line">
                    {aboutConfig.body}
                  </p>
                ) : (
                  <>
                    <p className="font-body text-base text-text-gray leading-relaxed">
                      I started as an electronics and computer engineering student at FUTO, drawn to how systems
                      work at their core. That curiosity led me to payment infrastructure — first at Unified
                      Payments, where I led implementations that put 5 million cards into the hands of everyday
                      Africans. Then to ProBASE, where I spent 14 years building the multi-rail payment platforms
                      that now process transactions for banks, governments, and telecom operators across Zambia.
                    </p>
                    <p className="font-body text-base text-text-gray leading-relaxed">
                      Today, as CTO at TechMasters Zambia and a partner at C2M Networks, I focus on the next
                      frontier: helping enterprises replace outdated processes with automation that actually works.
                      Alongside the technical work, I mentor founders at BongoHive, co-host the Three & Company
                      Podcast, and write about the systems — technical and human — that shape Africa's future.
                    </p>
                  </>
                )}
              </div>
            )}

            {show.quote !== false && aboutConfig.quote && (
              <blockquote
                className="anim-item mt-8 pl-6 border-l-[3px] border-accent-blue"
                style={{ opacity: 0, transform: 'translateY(30px)', transition: 'all 0.8s cubic-bezier(0.165, 0.840, 0.440, 1) 0.3s' }}
              >
                <p className="font-body text-lg text-text-primary italic leading-relaxed">
                  "{aboutConfig.quote}"
                </p>
              </blockquote>
            )}

            {show.tags !== false && (aboutConfig.tags?.length ?? 0) > 0 && (
              <div
                className="anim-item mt-8 flex flex-wrap gap-3"
                style={{ opacity: 0, transform: 'translateY(20px)', transition: 'all 0.6s ease-out 0.4s' }}
              >
                {aboutConfig.tags!.map((tag) => (
                  <span
                    key={tag}
                    className="px-3.5 py-1.5 bg-surface-gray border border-surface-border-gray rounded-full font-mono-label text-[10px] uppercase tracking-wider text-text-gray"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            <Link
              to="/about"
              className="inline-block text-accent-blue text-sm font-medium hover:underline mt-8"
            >
              Learn more →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
